import { useState } from "react"
import { Panel } from "../Panel"
import type { InfraCascade } from "../dashboard-types"

type InfraTab = "All" | "Cables" | "Pipelines" | "Ports"

const TABS: InfraTab[] = ["All", "Cables", "Pipelines", "Ports"]

/** Which icon tiles get the bright border for each tab. */
const TAB_HIGHLIGHTS: Record<InfraTab, string[]> = {
  All: [],
  Cables: ["Cables"],
  Pipelines: ["Pipelines"],
  Ports: ["Ports"],
}

/** Decorative dropdown options per tab. */
const TAB_OPTIONS: Record<InfraTab, string[]> = {
  All: [],
  Cables: ["AAE-1 (Asia-Africa-Europe)", "SEACOM (East Africa)", "FLAG Europe-Asia", "AJC (Aus-Japan)"],
  Pipelines: ["Druzhba (Europe)", "ESPO (Asia-Pacific)", "TurkStream (Black Sea)"],
  Ports: ["Port of Shanghai", "Port of Singapore", "Port of Rotterdam", "Port of LA/LB"],
}

const PLACEHOLDER: Record<InfraTab, string> = {
  All: "",
  Cables: "Select cable...",
  Pipelines: "Select pipeline...",
  Ports: "Select port...",
}

export function InfrastructureCascade({ data }: { data: InfraCascade }) {
  const [activeTab, setActiveTab] = useState<InfraTab>("All")

  const tiles: { icon: string; label: string; value: number }[] = [
    { icon: "⚓", label: "Ports", value: data.ports },
    { icon: "✈", label: "Airports", value: data.airports },
    { icon: "⫽", label: "Pipelines", value: data.pipelines },
    { icon: "⋯", label: "Cables", value: data.cables },
    { icon: "🏭", label: "Warehouses", value: data.warehouses },
    { icon: "▦", label: "Data centers", value: data.dcs },
  ]

  const highlighted = TAB_HIGHLIGHTS[activeTab]

  return (
    <Panel title="Infrastructure Cascade" count={data.total_nodes}>
      <div className="grid grid-cols-3 gap-0">
        {tiles.map((t) => {
          const isHighlighted = highlighted.includes(t.label)
          return (
            <div
              key={t.label}
              className={`px-3 py-3 border-r border-b text-center transition-colors ${
                isHighlighted
                  ? "border-[color:var(--accent)] bg-[color:rgba(34,211,238,0.06)]"
                  : "border-[color:var(--border)]"
              }`}
            >
              <div className="text-lg" style={{ color: "var(--accent)" }}>{t.icon}</div>
              <div className="text-lg tabular-nums text-[color:var(--text)] font-semibold mt-0.5">{t.value.toLocaleString()}</div>
              <div className="text-[9px] uppercase tracking-widest text-[color:var(--text-dim)] mt-0.5">{t.label}</div>
            </div>
          )
        })}
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-[color:var(--border)]">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            aria-pressed={activeTab === tab}
            onClick={() => setActiveTab(tab)}
            className="btn-pill text-[9px] px-2 py-0.5"
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Decorative dropdown */}
      {activeTab !== "All" && (
        <div className="px-3 py-1.5 border-b border-[color:var(--border)]">
          <select className="search-input text-[11px] py-1" defaultValue="">
            <option value="" disabled>
              {PLACEHOLDER[activeTab]}
            </option>
            {TAB_OPTIONS[activeTab].map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="px-3 py-2 text-[11px] text-[color:var(--text-muted)]">
        monitored links: <span className="tabular-nums text-[color:var(--text)]">{data.monitored_links.toLocaleString()}</span>
      </div>
    </Panel>
  )
}
